// Phase 2D.1B — client-side rules for the platform licensing console.
//
// Mirrors backend/quowada/platform_admin.py. THE BACKEND IS AUTHORITATIVE: this
// only gives immediate Bulgarian feedback so an obvious mistake does not need a
// round trip. Every rule here has a server-side twin, and the server-side twin
// wins.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

export const LICENSE_STATUSES = ["active", "suspended", "expired"];
export const EDITABLE_SEAT_ROLES = ["manager", "sales"];
export const OWNER_SEAT_LIMIT = 1;
export const MAX_INTERNAL_NOTE = 2000;
export const PAGE_SIZE = 25;

// Phase 2D.1C — public.org_lifecycle_status. "all" is the ABSENCE of a filter,
// never a state, which is why it is not part of LIFECYCLE_STATUSES.
export const LIFECYCLE_STATUSES = [
  "pending_activation",
  "active",
  "archived",
  "deletion_pending",
];
export const LIFECYCLE_FILTERS = ["all", ...LIFECYCLE_STATUSES];

// Mirrors quowada/platform_admin.py and the database CHECK constraints.
export const MAX_ORGANIZATION_NAME = 200;
export const MAX_PERSON_NAME = 100;
export const MIN_PHONE = 4;
export const MAX_PHONE = 40;
export const MAX_EMAIL = 320;

export const MESSAGES = {
  seat_not_integer: "Лимитът за места трябва да е цяло число.",
  seat_negative: "Лимитът за места не може да е отрицателен.",
  manager_below_usage:
    "Лимитът за мениджъри не може да е по-нисък от броя активни мениджъри.",
  sales_below_usage:
    "Лимитът за продавачи не може да е по-нисък от броя активни продавачи.",
  note_too_long: "Вътрешната бележка може да е до 2000 знака.",
  invalid_status: "Невалиден лицензионен статус.",
  invalid_date: "Въведете валидна дата.",
  name_required: "Въведете име на организацията.",
  name_too_long: "Името на организацията може да е до 200 знака.",
  first_name_invalid: "Въведете име на собственика (до 100 знака).",
  last_name_invalid: "Въведете фамилия на собственика (до 100 знака).",
  email_invalid: "Въведете валиден имейл адрес на собственика.",
  phone_invalid: "Телефонът трябва да е между 4 и 40 знака.",
};

const INTEGER_RE = /^\d+$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Whole, non-negative, and never below the active usage already displayed. */
export function validateSeatLimit(role, rawValue, activeUsage) {
  const text = typeof rawValue === "number" ? String(rawValue) : (rawValue || "").trim();
  if (!INTEGER_RE.test(text)) {
    return text.startsWith("-") ? MESSAGES.seat_negative : MESSAGES.seat_not_integer;
  }
  const value = Number(text);
  if (!Number.isInteger(value)) return MESSAGES.seat_not_integer;
  if (value < 0) return MESSAGES.seat_negative;
  if (Number.isFinite(activeUsage) && value < activeUsage) {
    return role === "manager" ? MESSAGES.manager_below_usage : MESSAGES.sales_below_usage;
  }
  return null;
}

export function validateInternalNote(rawValue) {
  const text = (rawValue || "").trim();
  return text.length > MAX_INTERNAL_NOTE ? MESSAGES.note_too_long : null;
}

// `valid_until` is stored as the EXCLUSIVE instant that ends the license, i.e.
// Europe/Sofia midnight of the day AFTER the last valid day. The console always
// shows and edits the last valid day itself, so both directions subtract or add
// exactly one Sofia day.
export function validUntilToInput(iso) {
  if (!iso) return "";
  const day = dayjs.utc(iso).tz("Europe/Sofia").subtract(1, "day");
  return day.isValid() ? day.format("YYYY-MM-DD") : "";
}

export function fmtValidUntil(iso, indefiniteLabel) {
  if (!iso) return indefiniteLabel;
  const day = dayjs.utc(iso).tz("Europe/Sofia").subtract(1, "day");
  return day.isValid() ? day.format("DD.MM.YYYY") : indefiniteLabel;
}

/** Bulgarian message the backend attached to a non-field error, if any. */
export function platformErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Phase 2D.1C — provisioning and the Owner invitation
// ---------------------------------------------------------------------------
export function validateOrganizationName(rawValue) {
  const text = (rawValue || "").trim();
  if (!text) return MESSAGES.name_required;
  return text.length > MAX_ORGANIZATION_NAME ? MESSAGES.name_too_long : null;
}

/** The four Owner contact fields. The phone is optional; everything else is not. */
export function validateOwnerContact(draft) {
  const errors = {};
  const first = (draft.owner_first_name || "").trim();
  const last = (draft.owner_last_name || "").trim();
  const email = (draft.owner_email || "").trim();
  const phone = (draft.owner_phone || "").trim();

  if (!first || first.length > MAX_PERSON_NAME) errors.owner_first_name = MESSAGES.first_name_invalid;
  if (!last || last.length > MAX_PERSON_NAME) errors.owner_last_name = MESSAGES.last_name_invalid;
  if (!email || email.length > MAX_EMAIL || !EMAIL_RE.test(email)) {
    errors.owner_email = MESSAGES.email_invalid;
  }
  if (phone && (phone.length < MIN_PHONE || phone.length > MAX_PHONE)) {
    errors.owner_phone = MESSAGES.phone_invalid;
  }
  return errors;
}

// Sanitized delivery codes the backend may store. Anything unknown falls back to
// the generic line, so a new code can never render an English internal label.
const DELIVERY_ERROR_KEYS = {
  email_disabled: "platform.delivery_error_email_disabled",
  security_sender_not_configured: "platform.delivery_error_security_sender",
  supabase_invite_rejected: "platform.delivery_error_supabase_rejected",
  delivery_failed: "platform.delivery_error_failed",
};

export function deliveryErrorKey(code) {
  return DELIVERY_ERROR_KEYS[code] || "platform.delivery_error_failed";
}
